import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { V2, pickLang } from "../copy";
import { Display, Eyebrow } from "./Primitives";
import DoorsV2 from "./DoorsV2";

/**
 * One plate per step. Line-art illustrations (transparent PNG) sit directly on
 * bone plates and are framed like a print on the teal / tangerine plates.
 */
const PLATES = [
  {
    cls: "bg-bone text-ink",
    img: "/illustrations/landing/composer.webp",
    framed: false,
    tilt: "rotate-[-3deg]",
  },
  {
    cls: "bg-teal-deep text-bone",
    img: "/illustrations/landing/collecte.webp",
    framed: true,
    tilt: "rotate-[2.5deg]",
  },
  {
    cls: "bg-tangerine text-bone",
    img: "/illustrations/landing/nettoyage.webp",
    framed: true,
    tilt: "rotate-[-2deg]",
  },
  {
    cls: "bg-bone text-ink",
    img: "/illustrations/landing/livraison.webp",
    framed: false,
    tilt: "rotate-[2deg]",
  },
];

/** Four sticky cards that stack as you scroll — the "method" as a deck. */
const ProcessV2: React.FC<{ hideHeader?: boolean }> = ({
  hideHeader = false,
}) => {
  const { currentLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const c = V2[lang].process;

  return (
    <section
      id="v2-process"
      className={`bg-ink text-bone ${hideHeader ? "py-16 sm:py-24" : "py-24 sm:py-32"}`}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        {!hideHeader && (
          <>
            <Eyebrow index="04">{c.label}</Eyebrow>
            <Display className="mt-5 max-w-4xl text-[clamp(2.4rem,6.4vw,6rem)]">
              {c.title}
            </Display>
          </>
        )}

        <div className={hideHeader ? "" : "mt-14"}>
          {c.steps.map((s, i) => {
            const plate = PLATES[i];
            return (
              <article
                key={s.n}
                className="v2-stack-card mb-5"
                style={{ ["--i" as any]: i }}
              >
                <div
                  className={`relative flex min-h-[56vh] flex-col overflow-hidden rounded-2xl p-6 sm:min-h-[60vh] sm:p-10 ${plate.cls}`}
                >
                  <span
                    className="pointer-events-none absolute -top-6 right-2 select-none font-display text-[34vw] leading-none opacity-[0.07] sm:text-[20vw]"
                    aria-hidden
                  >
                    {s.n}
                  </span>

                  <div className="relative flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.22em] opacity-70">
                    <span>{`${s.n} / 0${c.steps.length}`}</span>
                    <span>{s.meta}</span>
                  </div>

                  {/* illustration — sized by card height so it never meets the copy */}
                  <div className="pointer-events-none absolute right-4 top-[13%] flex h-[46%] justify-end sm:right-10 sm:h-[52%]">
                    {plate.framed ? (
                      <div
                        className={`h-full rounded-xl bg-bone p-2 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] ${plate.tilt}`}
                      >
                        <img
                          src={plate.img}
                          alt=""
                          loading="lazy"
                          className="h-full w-auto rounded-lg object-contain"
                        />
                      </div>
                    ) : (
                      <img
                        src={plate.img}
                        alt=""
                        loading="lazy"
                        className={`h-full w-auto object-contain ${plate.tilt}`}
                      />
                    )}
                  </div>

                  <div className="relative mt-auto grid items-end gap-6 pt-[50%] sm:grid-cols-12 sm:pt-[38%]">
                    <h3 className="font-display text-[clamp(2.2rem,6vw,5.5rem)] leading-[0.95] tracking-[-0.02em] sm:col-span-7">
                      {s.title}
                    </h3>
                    <p className="max-w-sm text-base leading-relaxed opacity-75 sm:col-span-5 sm:text-lg">
                      {s.desc}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <DoorsV2 className="mt-6" />
      </div>
    </section>
  );
};

export default ProcessV2;
